import { Button, Card, Result, Space, Table, TableProps } from "antd";
import React, { Key, useCallback, useMemo } from "react";
import { useGetStatementTitleGroupsListQuery, useGetStatementTitlesListQuery } from "redux/api/reconciliationsApiSlice";
import { CreditDebitStatus, GetStatementTitleListParams, StatementTitle } from "types/reconciliation";
import BrokerFilter from "../../tables/negotiationsTables/BrokerFilter";
import { useSelector } from "react-redux";
import { selectOptions } from "../../../redux/features/optionsSlice";
import {
  getColumnDateProps,
  getNumberRangeFilter,
  getStandardFilter
} from "components/tables/negotiationsTables/columnsUtils";
import FilterBySearch from "../../tables/negotiationsTables/generalTable/FilterBySearch";
import { isEmpty } from "lodash";
import { StatementTitlesTableProvider, useStatementsTitlesTable } from "context/StatementTitlesTableContext";
import LinkedEmittedTitlesTable from "../linkedEmittedTitlesTable/LinkedEmittedTitlesTable";
import { LinkedEmittedTableProvider } from "../../../context/LinkedEmittedTableContext";
import { ERROR_COLOR, formatTmpPercentage, printValue, RECONCILIATION_COLOR } from "utils/reconciliationTableUtils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { StatementTitleGroupStatementTitlesTable } from "./StatementTitleGroupStatementTitlesTable";
import { NegotiationEmittedTableProvider } from "../../../context/NegotiationEmittedTableContext";
import NegotiationEmittedTitlesTable from "../negotiationEmittedTable/NegotiationEmittedTitlesTable";

/**
 * A React component that renders a table displaying statement titles along with filtering, sorting,
 * and pagination capabilities.
 *
 * @param {object} props - The properties for the component.
 * @param {GetStatementTitleListParams} [props.defaultFilters] - Default filters to apply when fetching the statement titles list.
 * @param {TableProps<StatementTitle>} [props.tableProps] - Additional table properties to customize the behavior of the table.
 * @param {boolean} [props.isReconciliation] - Flag to indicate if the table is in reconciliation mode.
 * @param {(selectedRowKeys: React.Key[]) => void} [props.onSelectedRowsChange] - Callback when selected rows change.
 *
 * @return {React.ReactElement} A React element representing the titles table.
 */
const CREDIT_DEBIT_STATUS_FILTER = Object.keys(CreditDebitStatus).map(el => ({ text: el, value: el })) as {
  text: string,
  value: string
}[];

export default function StatementTitlesTable({
                                               defaultFilters,
                                               tableProps,
                                               showReconciliationColumns = false,
                                               showConfirmedTitlesColumn = false,
                                               selectedRows = [],
                                               onSelectedRowsChange,
                                               rowsSelectable = false,
                                               allRowsSelectable = false,
                                               useStatementsTitlesGroup = true
                                             }: {
  defaultFilters?: GetStatementTitleListParams,
  tableProps?: TableProps<StatementTitle>,
  showReconciliationColumns?: boolean,
  showConfirmedTitlesColumn?: boolean,
  rowsSelectable?: boolean,
  selectedRows?: React.Key[],
  onSelectedRowsChange?: (selectedRowKeys: React.Key[], selectedRecords: StatementTitle[]) => void,
  allRowsSelectable?: boolean,
  useStatementsTitlesGroup?: boolean
}) {
  const options = useSelector(selectOptions);
  const {
    page,
    pageSize,
    broker,
    lob,
    ecNumber,
    ecPeriod,
    titleReceiptDate,
    titleEffectiveDate,
    policyNumber,
    credit,
    creditDelta,
    creditDifference,
    creditStatus,
    creditDebitStatus,
    debit,
    debitDelta,
    debitDifference,
    debitStatus,
    contractor,
    confirmed,
    isSingle,
    titleNumber,
    setPage,
    setPageSize,
    setOrdering,
    setBroker,
    setLob,
    setEcNumber,
    setEcPeriod,
    setTitleEffectiveDate,
    setTitleReceiptDate,
    setCredit,
    setCreditDelta,
    setCreditStatus,
    setDebit,
    setDebitDelta,
    setDebitStatus,
    setCreditDebitStatus,
    setContractor,
    setPolicyNumber,
    setConfirmed,
    setIsSingle,
    setTitleNumber,
    getParamsForApiRequest
  } = useStatementsTitlesTable();

  const {
    data: statementTitlesList,
    isLoading: isStatementTitlesListFetchLoading,
    isFetching: isStatementTitlesListFetching,
    isError: hasStatementTitlesListFetchFailed,
    refetch: refetchStatementTitlesList
  } = useGetStatementTitlesListQuery(!useStatementsTitlesGroup ? getParamsForApiRequest(defaultFilters) : skipToken);

  const {
    data: statementTitleGroupsList,
    isLoading: isStatementTitleGroupsListFetchLoading,
    isFetching: isStatementTitleGroupsListFetching,
    isError: hasStatementTitleGroupsListFetchFailed,
    refetch: refetchStatementTitleGroupsList
  } = useGetStatementTitleGroupsListQuery(useStatementsTitlesGroup ? getParamsForApiRequest(defaultFilters) : skipToken);

  // Gestisce il cambio di selezione delle righe
  const handleRowSelectionChange = useCallback((selectedKeys: React.Key[], records: StatementTitle[]) => {
    // Notifica il componente padre del cambiamento se la funzione è fornita
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedKeys, records);
    }
  }, [onSelectedRowsChange]);

  // Configurazione per la selezione delle righe
  const rowSelection = useMemo(() => {
    //if (!showReconciliationColumns) return undefined;

    return {
      selectedRowKeys: selectedRows,
      onChange: handleRowSelectionChange,
      // Verifica se la riga può essere selezionata in base allo stato di riconciliazione
      getCheckboxProps: (record: StatementTitle) => ({
        disabled: allRowsSelectable ? false : (![CreditDebitStatus.OK, CreditDebitStatus.WARNING, CreditDebitStatus.KO].includes(record.credit_debit_status) || record.confirmed),
        name: record.uuid
      }),
      // Preserva le selezioni quando si cambia pagina
      preserveSelectedRowKeys: true
    };
  }, [selectedRows, handleRowSelectionChange, allRowsSelectable]);

  const cols: TableProps<StatementTitle>["columns"] = useMemo(() => ([
    ...(showReconciliationColumns ? [{
      key: "is_single",
      dataIndex: "is_single",
      title: "Aggregazione",
      filteredValue: isSingle,
      width: 140,
      ellipsis: true,
      render: (value: boolean) => {
        return <>
          {value ? "Singolo" : "Gruppo"}
        </>;
      },
      ...getStandardFilter([{ text: "Singolo", value: true }, { text: "Gruppo", value: false }])
    }] : []),
    ...(showConfirmedTitlesColumn ? [
      {
        key: "confirmed",
        dataIndex: "confirmed",
        title: "In Rimessa",
        filteredValue: confirmed,
        width: 140,
        ellipsis: true,
        render: (value: any) => {
          return <>
            {value ? "Sì" : "No"}
          </>;
        },
        ...getStandardFilter([{ text: "Sì", value: true }, { text: "No", value: false }])
      }
    ] : []),
    {
      key: "ec_period",
      dataIndex: "ec_period",
      title: "Data EC",
      sorter: { multiple: 1 },
      filteredValue: ecPeriod,
      ...getColumnDateProps("ec_period", "month"),
      ellipsis: true,
      onCell: (record) => ({
        style: { ...(!record.ec_period_validation && { background: ERROR_COLOR }) }
      }),
      render: (value, record) => printValue(value, "date", {
        dateTemplate: "MMMM YYYY",
        isSingle: record.is_single
      })
    },
    {
      key: "ec_number",
      dataIndex: "ec_number",
      title: "Numero EC",
      sorter: { multiple: 2 },
      onCell: (record) => ({
        style: { ...(!record.ec_number_validation && { background: ERROR_COLOR }) }
      }),
      filteredValue: ecNumber,
      filterDropdown: (props) => {
        return <FilterBySearch props={props}
                               placeholder={"Filtra per numero estratto conto..."} />;
      },
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "broker",
      dataIndex: "broker",
      title: "Broker",
      filteredValue: broker,
      filterDropdown: (props) => BrokerFilter({ ...props, isReconciliationBroker: true }),
      sorter: { multiple: 3 },
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "policy_number",
      dataIndex: "policy_number",
      title: "Codice contratto",
      sorter: { multiple: 4 },
      filteredValue: policyNumber,
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per il codice contratto..."} />;
      },
      onCell: (record) => ({
        style: { ...(!record.policy_number_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "title_number",
      dataIndex: "title_number",
      title: "Codice titolo",
      filteredValue: titleNumber,
      sorter: { multiple: 5 },
      onCell: (record) => ({
        style: { ...(!record.title_number_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      filterDropdown: (props) => {
        return <FilterBySearch props={props}
                               placeholder={"Filtra per codice titolo..."} />;
      },
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "lob",
      dataIndex: "lob",
      title: "Prodotto",
      sorter: { multiple: 6 },
      filteredValue: lob,
      ...getStandardFilter(options.lobs.map(el => ({ text: el.name, value: el.uuid }))),
      onCell: (record) => ({
        style: { ...(!record.lob_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "contractor",
      dataIndex: "contractor",
      title: "Contraente",
      sorter: { multiple: 7 },
      filteredValue: contractor,
      ellipsis: true,
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per il nome del contraente..."} />;
      },
      onCell: (record) => ({
        style: { ...(!record.contractor_validation && { background: ERROR_COLOR }) }
      }),
      render: (value, record) => printValue(value, "text", { isSingle: record.is_single })
    },
    {
      key: "title_effective_date",
      dataIndex: "title_effective_date",
      title: "Data effetto titolo",
      sorter: { multiple: 8 },
      filteredValue: titleEffectiveDate,
      ...getColumnDateProps("title_effective_date", "day"),
      onCell: (record) => ({
        style: { ...(!record.title_effective_date_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", {
        dateTemplate: "DD/MM/YYYY",
        isSingle: record.is_single
      })
    },
    {
      key: "title_receipt_date",
      dataIndex: "title_receipt_date",
      title: "Data incasso titolo",
      sorter: { multiple: 9 },
      filteredValue: titleReceiptDate,
      ...getColumnDateProps("title_receipt_date", "day"),
      onCell: (record) => ({
        style: { ...(!record.title_receipt_date_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", {
        dateTemplate: "DD/MM/YYYY",
        isSingle: record.is_single
      })
    },
    {
      key: "credit",
      dataIndex: "credit",
      title: "Premio lordo",
      className: "justify-right",
      sorter: { multiple: 10 },
      filteredValue: credit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      onCell: (record) => ({
        style: { ...(!record.credit_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "currency", { isSingle: record.is_single })
    },
    {
      key: "debit",
      dataIndex: "debit",
      title: "Provvigioni",
      className: "justify-right",
      sorter: { multiple: 11 },
      filteredValue: debit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      onCell: (record) => ({
        style: { ...(!record.debit_validation && { background: ERROR_COLOR }) }
      }),
      ellipsis: true,
      render: (value, record) => printValue(value, "currency", { isSingle: record.is_single })
    },
    ...showReconciliationColumns ? [
      {
        key: "credit_debit_status",
        dataIndex: "credit_debit_status",
        title: "Stato riconciliazione",
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        }),
        ...getStandardFilter(CREDIT_DEBIT_STATUS_FILTER),
        filteredValue: creditDebitStatus
      },
      {
        key: "credit_status",
        dataIndex: "credit_status",
        title: "Stato premio lordo",
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        }),
        ...getStandardFilter(CREDIT_DEBIT_STATUS_FILTER),
        filteredValue: creditStatus
      },
      {
        key: "credit_difference",
        dataIndex: "credit_difference",
        title: "Premio lordo differenza",
        className: "justify-right",
        sorter: { multiple: 12 },
        filteredValue: creditDifference,
        ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
        render: (value: string, record: StatementTitle) => printValue(value, "currency", { isSingle: false }),
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        })
      },
      {
        key: "credit_delta",
        dataIndex: "credit_delta",
        title: "Premio lordo delta",
        className: "justify-right",
        sorter: { multiple: 13 },
        filteredValue: creditDelta,
        ...getNumberRangeFilter(0, 500, 0.1, "percentage"),
        // @ts-ignore
        render: (value) => formatTmpPercentage(value),
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        })
      },

      {
        key: "debit_status",
        dataIndex: "debit_status",
        title: "Stato provvigioni",
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        }),
        ...getStandardFilter(CREDIT_DEBIT_STATUS_FILTER),
        filteredValue: debitStatus
      },
      {
        key: "debit_difference",
        dataIndex: "debit_difference",
        title: "Provvigioni differenza",
        className: "justify-right",
        sorter: { multiple: 14 },
        filteredValue: debitDifference,
        ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        }),
        render: (value: string, record: StatementTitle) => printValue(value, "currency", { isSingle: false })
      },
      {
        key: "debit_delta",
        dataIndex: "debit_delta",
        title: "Provvigioni delta",
        className: "justify-right",
        sorter: { multiple: 15 },
        filteredValue: debitDelta,
        ...getNumberRangeFilter(0, 500, 0.1, "percentage"),
        onCell: () => ({
          style: { backgroundColor: RECONCILIATION_COLOR }
        }),
        // @ts-ignore
        render: (value) => formatTmpPercentage(value)
      }
    ] : []
  ] as TableProps<StatementTitle>["columns"]), [showReconciliationColumns, isSingle, showConfirmedTitlesColumn, confirmed, broker, ecPeriod, ecNumber, policyNumber, lob, options.lobs, titleEffectiveDate, contractor, credit, debit, titleNumber, titleReceiptDate, creditDebitStatus, creditStatus, creditDelta, creditDifference, debitStatus, debitDelta, debitDifference]);

  return (
    <>
      {(hasStatementTitlesListFetchFailed || hasStatementTitleGroupsListFetchFailed) && (
        <Result status={"warning"} title={"Errore nello scaricamento dei titoli"} extra={
          <Button type={"primary"}
                  onClick={hasStatementTitlesListFetchFailed ? refetchStatementTitlesList : refetchStatementTitleGroupsList}>
            Aggiorna
          </Button>
        } />
      )}
      {!hasStatementTitlesListFetchFailed && <>
        <Table
          <StatementTitle>
          className={"reconciliation-table"}
          dataSource={(useStatementsTitlesGroup) ? statementTitleGroupsList?.results : statementTitlesList?.results}
          columns={cols}
          loading={isStatementTitlesListFetchLoading || isStatementTitlesListFetching || isStatementTitleGroupsListFetching || isStatementTitleGroupsListFetchLoading}
          rowSelection={rowsSelectable ? rowSelection : undefined}
          expandable={{
            expandedRowRender: (record) => <div style={{ margin: "0.5rem 0" }}>
              <Space direction={"vertical"}>
                {
                  !record.is_single && <Card title={"Titoli EC"}>
                    <StatementTitlesTableProvider>
                      <StatementTitleGroupStatementTitlesTable statementTitleGroup={record.uuid} />
                    </StatementTitlesTableProvider>
                  </Card>}

                <Card title={"Titoli Arretrati"}>
                  <LinkedEmittedTableProvider>
                    <LinkedEmittedTitlesTable uuid={record.uuid} />
                  </LinkedEmittedTableProvider>
                </Card>

                <Card title={"Trattative"}>
                  <NegotiationEmittedTableProvider>
                    <NegotiationEmittedTitlesTable uuid={record.uuid} />
                  </NegotiationEmittedTableProvider>
                </Card>

              </Space>
            </div>,
            rowExpandable: (record) => (record.matched && record.credit_debit_status !== null) || !record.is_single,
            showExpandColumn: showReconciliationColumns
          }}
          rowKey={"uuid"}
          scroll={{ x: true }}
          pagination={{
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            total: (statementTitleGroupsList?.count ?? statementTitlesList?.count) ?? 0,
            showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} titoli`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            }
          }}
          onChange={(_, filters, sorter) => {
            // Gestione ordinamento
            const buildOrderingString = (sorterValue: any): string => {
              if (Array.isArray(sorterValue)) {
                return sorterValue
                  .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
                  .join(",");
              } else if (sorterValue.order) {
                return sorterValue.order === "descend" ? `-${sorterValue.field}` : `${sorterValue.field}`;
              }
              return "";
            };

            // Applicazione ordinamento
            setOrdering(buildOrderingString(sorter));

            // Gestione filtri
            if (filters) {
              // Mappa delle funzioni setter per ogni tipo di filtro
              const filterSetters: Record<string, (value: Key[]) => void> = {
                broker: setBroker,
                ec_period: setEcPeriod,
                ec_number: setEcNumber,
                lob: setLob,
                title_effective_date: setTitleEffectiveDate,
                contractor: setContractor,
                credit: setCredit,
                credit_delta: setCreditDelta,
                credit_status: setCreditStatus,
                debit: setDebit,
                debit_delta: setDebitDelta,
                debit_status: setDebitStatus,
                credit_debit_status: setCreditDebitStatus,
                title_receipt_date: setTitleReceiptDate,
                policy_number: setPolicyNumber,
                confirmed: setConfirmed,
                is_single: setIsSingle,
                title_number: setTitleNumber
              };

              // Applica i setter per ogni filtro
              Object.entries(filterSetters).forEach(([filterName, setter]) => {
                const filterValue = filters[filterName];
                setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
              });
            }
          }}
          {...tableProps}
        />
      </>}
    </>
  );
}